import "../styles/calendar.css"
import "react-calendar/dist/Calendar.css"

import dayjs from "dayjs"
import isoWeek from "dayjs/plugin/isoWeek"
import relativeTime from "dayjs/plugin/relativeTime"
import weekOfYear from "dayjs/plugin/weekOfYear"
import React, { useMemo, useState } from "react"
import Calendar from "react-calendar"

import { useAuth } from "../lib/auth-context"
import { trpc } from "../lib/trpc"

dayjs.extend(relativeTime)
dayjs.extend(weekOfYear)
dayjs.extend(isoWeek)

interface MetricEvent {
  id: string
  category: string
  type: string
  value: string | null
  unit: string | null
  data: Record<string, any>
  eventDate: string
  eventTime: string
  recordedAt: string
  chatId: number
  rawInput: string | null
  confidence: string | null
  tags: string[]
  status?: "planned" | "completed" | "skipped" | "cancelled"
  plannedAt?: string | null
  priority?: string | null
}

interface CategoryStats {
  totalEvents: number
  types: Record<string, number>
  recentEvents: MetricEvent[]
}

type ViewMode = "list" | "calendar"
type ShowMode = "all" | "latest"

const MetricsScreen = () => {
  const { user } = useAuth()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>("list")
  const [showMode, setShowMode] = useState<ShowMode>("latest")
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(7, "days").format("YYYY-MM-DD"),
    endDate: dayjs().add(7, "days").format("YYYY-MM-DD"),
  })

  // Fetch metrics
  const {
    data: rawMetricsData,
    isLoading,
    error,
    refetch,
  } = trpc.metrics.query.useQuery({
    startDate: `${dateRange.startDate}T00:00:00Z`,
    endDate: `${dateRange.endDate}T23:59:59Z`,
  })

  // Delete metric mutation
  const deleteMetricMutation = trpc.metrics.delete.useMutation({
    onSuccess: () => {
      void refetch() // Refresh the metrics data
    },
    onError: (error) => {
      console.error("Error deleting metric:", error)
      alert("Failed to delete metric: " + error.message)
    },
  })

  const handleDeleteMetric = (event: MetricEvent) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this metric?\n\n${formatValue(event)}\n\nThis action cannot be undone.`,
    )

    if (confirmDelete) {
      deleteMetricMutation.mutate({ eventId: event.id })
    }
  }

  // Filter metrics based on show mode
  const metricsData = useMemo(() => {
    if (!rawMetricsData) return undefined
    if (showMode === "all") return rawMetricsData

    // Group by category/type/date and take latest
    const grouped: Record<string, MetricEvent> = {}

    rawMetricsData.forEach((metric) => {
      // For task-based categories (vidmax, agent-platform), include task/feature name in the key
      let key = `${metric.category}-${metric.type}-${metric.eventDate}`

      // Helper function to normalize task/feature names for consistent deduplication
      const normalizeTaskName = (name: string): string => {
        return name
          .toLowerCase()
          .trim()
          .replace(/\s+/g, "_") // Replace spaces with underscores
          .replace(/[^a-z0-9_]/g, "") // Remove special characters
          .substring(0, 50) // Limit length
      }

      // Safely access data properties
      const metricData = metric.data || {}

      if (
        (metric.category === "vidmax" || metric.category === "agent-platform") &&
        (metricData.task_name || metricData.feature_name)
      ) {
        const taskName = metricData.task_name || metricData.feature_name
        key += `-task:${normalizeTaskName(String(taskName))}`
      } else if (metric.category === "business" && metricData.metric_name) {
        key += `-metric:${normalizeTaskName(String(metricData.metric_name))}`
      }
      // For other categories with specific identifiers in description
      else if (metricData.description && ["fitness", "nutrition"].includes(metric.category)) {
        // Use first 30 chars of description as part of key for uniqueness
        const descKey = normalizeTaskName(String(metricData.description).substring(0, 30))
        key += `-desc:${descKey}`
      }

      if (!grouped[key] || new Date(metric.recordedAt) > new Date(grouped[key].recordedAt)) {
        grouped[key] = metric
      }
    })

    return Object.values(grouped)
  }, [rawMetricsData, showMode])

  // Group metrics by category
  const categorizedMetrics = useMemo(() => {
    if (!metricsData) return {}

    const grouped: Record<string, CategoryStats> = {}

    metricsData.forEach((metric: MetricEvent) => {
      if (!grouped[metric.category]) {
        grouped[metric.category] = {
          totalEvents: 0,
          types: {},
          recentEvents: [],
        }
      }

      const category = grouped[metric.category]
      category.totalEvents++
      category.types[metric.type] = (category.types[metric.type] || 0) + 1
      category.recentEvents.push(metric)
    })

    // Sort recent events and limit to 10 per category
    Object.keys(grouped).forEach((category) => {
      grouped[category].recentEvents.sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime())
      grouped[category].recentEvents = grouped[category].recentEvents.slice(0, 10)
    })

    return grouped
  }, [metricsData])

  // Calculate weekly summary for time-tracked categories
  const weeklySummary = useMemo(() => {
    if (!metricsData) return null

    const timeCategories = ["vidmax", "agent-platform"]
    const summary: Record<string, { planned: number; actual: number; tasks: number }> = {}

    // Initialize summary
    timeCategories.forEach((cat) => {
      summary[cat] = { planned: 0, actual: 0, tasks: 0 }
    })

    // Process metrics
    metricsData.forEach((metric: MetricEvent) => {
      if (!timeCategories.includes(metric.category)) return

      if (metric.data?.actual_hours) {
        const hours = parseFloat(String(metric.data.actual_hours))
        if (!isNaN(hours)) {
          summary[metric.category].actual += hours
          summary[metric.category].tasks++
        }
      } else if (metric.data?.estimated_hours && metric.status === "planned") {
        const hours = parseFloat(String(metric.data.estimated_hours))
        if (!isNaN(hours)) {
          summary[metric.category].planned += hours
        }
      }
    })

    // Calculate totals
    const totals = {
      planned: Object.values(summary).reduce((acc, cat) => acc + cat.planned, 0),
      actual: Object.values(summary).reduce((acc, cat) => acc + cat.actual, 0),
      tasks: Object.values(summary).reduce((acc, cat) => acc + cat.tasks, 0),
    }

    return { categories: summary, totals }
  }, [metricsData])

  const categories = Object.keys(categorizedMetrics)

  // Group events by week and day for selected category
  const groupedByWeek = useMemo(() => {
    if (!selectedCategory || !categorizedMetrics[selectedCategory]) return {}

    const events = metricsData?.filter((e) => e.category === selectedCategory) || []
    const grouped: Record<string, Record<string, MetricEvent[]>> = {}

    events.forEach((event) => {
      const date = dayjs(event.eventDate)
      // Start week on Monday
      const weekStart = date.startOf("isoWeek")
      const weekKey = weekStart.format("YYYY-MM-DD")
      const dayKey = date.format("YYYY-MM-DD")

      if (!grouped[weekKey]) {
        grouped[weekKey] = {}
      }
      if (!grouped[weekKey][dayKey]) {
        grouped[weekKey][dayKey] = []
      }
      grouped[weekKey][dayKey].push(event)
    })

    // Sort events within each day
    Object.values(grouped).forEach((week) => {
      Object.values(week).forEach((dayEvents) => {
        dayEvents.sort((a, b) => new Date(b.eventTime).getTime() - new Date(a.eventTime).getTime())
      })
    })

    return grouped
  }, [selectedCategory, categorizedMetrics, metricsData])

  const formatValue = (metric: MetricEvent) => {
    if (metric.value && metric.unit) {
      return `${metric.value} ${metric.unit}`
    }

    // Try to extract key values from data object
    if (metric.data) {
      if (metric.category === "fitness" && metric.data.duration_minutes) {
        return `${metric.data.duration_minutes} minutes`
      }
      if (metric.category === "nutrition" && metric.data.calories) {
        return `${metric.data.calories} calories`
      }
      if (metric.category === "vidmax") {
        const taskName = metric.data.task_name || metric.data.feature_name || metric.type
        const estimated = metric.data.estimated_hours
        const actual = metric.data.actual_hours

        if (actual && estimated) {
          return `${taskName} • Actual: ${actual}h | Est: ${estimated}h`
        } else if (actual) {
          return `${taskName} • Worked: ${actual}h`
        } else if (estimated) {
          return `${taskName} • Est: ${estimated}h`
        }
        return taskName || metric.data.description || metric.type
      }
      if (metric.category === "agent-platform") {
        const taskName = metric.data.task_name || metric.data.feature_name || metric.type
        const estimated = metric.data.estimated_hours
        const actual = metric.data.actual_hours

        if (actual && estimated) {
          return `${taskName} • Actual: ${actual}h | Est: ${estimated}h`
        } else if (actual) {
          return `${taskName} • Worked: ${actual}h`
        } else if (estimated) {
          return `${taskName} • Est: ${estimated}h`
        }
        return taskName || metric.data.description || metric.type
      }
    }

    return metric.rawInput?.substring(0, 50) || "No value"
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "fitness":
        return "💪"
      case "nutrition":
        return "🍽️"
      case "vidmax":
        return "🎥"
      case "agent-platform":
        return "🤖"
      default:
        return "📊"
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "fitness":
        return "bg-blue-500"
      case "nutrition":
        return "bg-green-500"
      case "vidmax":
        return "bg-red-500"
      case "agent-platform":
        return "bg-indigo-500"
      default:
        return "bg-foreground0"
    }
  }

  if (isLoading) {
    return (
      <div className="p-3 sm:p-4 lg:p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-foregroundx rounded w-1/4 mb-3"></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-foregroundx rounded"></div>
            ))}
          </div>
          <div className="h-40 bg-foregroundx rounded"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-3 sm:p-4 lg:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-destructive">Error loading metrics: {error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="mb-3 sm:mb-4">
        <h1 className="text-xl sm:text-2xl font-bold ">Metrics Dashboard</h1>
        <p className="text-xs sm:text-sm ">Track your personal metrics across all categories</p>
      </div>

      {/* View Mode Toggle and Date Range Selector */}
      <div className="mb-3 sm:mb-4">
        <div className="flex flex-wrap justify-between items-start gap-2 mb-3">
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-1 text-xs sm:text-sm rounded-md transition-colors ${
                viewMode === "list" ? "bg-blue-500 text-white" : "bg-foregroundx  text-primary"
              }`}
            >
              📋 List View
            </button>
            <button
              onClick={() => setViewMode("calendar")}
              className={`px-3 py-1 text-xs sm:text-sm rounded-md transition-colors ${
                viewMode === "calendar" ? "bg-blue-500 text-white" : "bg-foregroundx  text-primary"
              }`}
            >
              📅 Calendar View
            </button>
            <div className="ml-2 pl-2 border-l border-gray-300 flex gap-1">
              <button
                onClick={() => setShowMode("latest")}
                className={`px-3 py-1 text-xs sm:text-sm rounded-md transition-colors ${
                  showMode === "latest" ? "bg-green-500 text-white" : "bg-foregroundx  text-primary"
                }`}
              >
                Latest Only
              </button>
              <button
                onClick={() => setShowMode("all")}
                className={`px-3 py-1 text-xs sm:text-sm rounded-md transition-colors ${
                  showMode === "all" ? "bg-green-500 text-white" : "bg-foregroundx  text-primary"
                }`}
              >
                Show All
              </button>
            </div>
          </div>

          {viewMode === "list" && (
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex flex-col sm:flex-row gap-2">
                <div>
                  <label className="block text-xs font-medium text-primary mb-0.5">Start</label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                    className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-primary mb-0.5">End</label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                    className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() =>
                    setDateRange({
                      startDate: dayjs().subtract(30, "days").format("YYYY-MM-DD"),
                      endDate: dayjs().add(30, "days").format("YYYY-MM-DD"),
                    })
                  }
                  className="px-2 py-1 text-xs bg-foregroundx  rounded-md"
                >
                  ±30d
                </button>
                <button
                  onClick={() =>
                    setDateRange({
                      startDate: dayjs().subtract(7, "days").format("YYYY-MM-DD"),
                      endDate: dayjs().add(7, "days").format("YYYY-MM-DD"),
                    })
                  }
                  className="px-2 py-1 text-xs bg-foregroundx  rounded-md"
                >
                  ±7d
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filter Info */}
      {showMode === "latest" && rawMetricsData && metricsData && rawMetricsData.length > metricsData.length && (
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-xs text-blue-700">
            Showing latest entries only. {rawMetricsData.length - metricsData.length} duplicate
            {rawMetricsData.length - metricsData.length !== 1 ? "s" : ""} hidden.
          </p>
        </div>
      )}

      {/* Weekly Time Summary */}
      {weeklySummary && weeklySummary.totals.actual > 0 && (
        <div className="mb-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold  mb-3 flex items-center gap-2">
            <span>⏱️</span>
            Time Tracking Summary
            <span className="text-xs font-normal ">
              ({dayjs(dateRange.startDate).format("MMM D")} - {dayjs(dateRange.endDate).format("MMM D")})
            </span>
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            {Object.entries(weeklySummary.categories).map(([category, data]) => {
              if (data.actual === 0 && data.planned === 0) return null
              const efficiency = data.planned > 0 ? Math.round((data.actual / data.planned) * 100) : 0

              return (
                <div key={category} className="bg-background rounded-md p-3 border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getCategoryIcon(category)}</span>
                      <h4 className="text-sm font-medium capitalize">{category}</h4>
                    </div>
                    {data.tasks > 0 && <span className="text-xs text-gray-500">{data.tasks} tasks</span>}
                  </div>

                  <div className="space-y-1">
                    {data.actual > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs ">Actual:</span>
                        <span className="text-sm font-semibold text-green-600">{data.actual.toFixed(1)}h</span>
                      </div>
                    )}
                    {data.planned > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs ">Planned:</span>
                        <span className="text-sm text-primary">{data.planned.toFixed(1)}h</span>
                      </div>
                    )}
                    {data.planned > 0 && data.actual > 0 && (
                      <div className="mt-1 pt-1 border-t border-gray-100">
                        <div className="flex justify-between items-center">
                          <span className="text-xs ">Efficiency:</span>
                          <span
                            className={`text-sm font-medium ${
                              efficiency >= 100
                                ? "text-green-600"
                                : efficiency >= 75
                                  ? "text-yellow-600"
                                  : "text-destructive"
                            }`}
                          >
                            {efficiency}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Total Summary */}
          <div className="bg-background rounded-md p-3 border border-gray-300">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xs ">Total Worked</p>
                <p className="text-lg font-bold text-green-600">{weeklySummary.totals.actual.toFixed(1)}h</p>
              </div>
              <div>
                <p className="text-xs ">Total Planned</p>
                <p className="text-lg font-bold text-primary">{weeklySummary.totals.planned.toFixed(1)}h</p>
              </div>
              <div>
                <p className="text-xs ">Completion</p>
                <p className="text-lg font-bold text-purple-600">
                  {weeklySummary.totals.planned > 0
                    ? Math.round((weeklySummary.totals.actual / weeklySummary.totals.planned) * 100)
                    : 100}
                  %
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Overview Cards - Always show */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 mb-4">
        {categories.map((category) => {
          const stats = categorizedMetrics[category]
          return (
            <div
              key={category}
              className={`bg-background rounded-lg shadow-sm p-3 cursor-pointer transition-all hover:shadow-md ${
                selectedCategory === category ? "ring-2 ring-blue-500" : ""
              }`}
              onClick={() => setSelectedCategory(category === selectedCategory ? null : category)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{getCategoryIcon(category)}</span>
                  <h3 className="text-sm font-semibold capitalize">{category}</h3>
                </div>
                <span className={`px-1.5 py-0.5 text-xs text-white rounded-full ${getCategoryColor(category)}`}>
                  {stats.totalEvents}
                </span>
              </div>
              <div className="space-y-0.5">
                <p className="text-xs ">{Object.keys(stats.types).length} types</p>
                <p className="text-xs text-gray-500 truncate">
                  {stats.recentEvents?.length > 0 ? dayjs(stats.recentEvents[0].recordedAt).fromNow() : "No events"}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* View Mode Rendering */}
      {viewMode === "calendar" ? (
        // Calendar View
        <div className="bg-background rounded-lg shadow-md p-3 sm:p-4">
          <div className="react-calendar-wrapper">
            <Calendar
              value={selectedDate}
              onChange={(value) => setSelectedDate(value as Date)}
              className="w-full border-0"
              tileContent={({ date }) => {
                const dateKey = dayjs(date).format("YYYY-MM-DD")
                const dayEvents = metricsData?.filter((e) => e.eventDate === dateKey) || []
                const categoryEvents = selectedCategory
                  ? dayEvents.filter((e) => e.category === selectedCategory)
                  : dayEvents

                if (categoryEvents.length === 0) return null

                const plannedCount = categoryEvents.filter((e) => e.status === "planned").length
                const completedCount = categoryEvents.filter((e) => e.status === "completed").length

                return (
                  <div className="text-xs mt-1">
                    <div className="flex flex-wrap gap-0.5 justify-center">
                      {selectedCategory ? (
                        <>
                          <span
                            className={`inline-block w-1.5 h-1.5 rounded-full ${getCategoryColor(selectedCategory)}`}
                          />
                          <span className="">{categoryEvents.length}</span>
                          {plannedCount > 0 && (
                            <span className="text-green-600 font-semibold">
                              {completedCount}/{plannedCount}
                            </span>
                          )}
                        </>
                      ) : (
                        // Show dots for each category when no category selected
                        Array.from(new Set(categoryEvents.map((e) => e.category))).map((cat) => (
                          <span
                            key={cat}
                            className={`inline-block w-1.5 h-1.5 rounded-full ${getCategoryColor(cat)}`}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )
              }}
              tileClassName={({ date }) => {
                const dateKey = dayjs(date).format("YYYY-MM-DD")
                const dayEvents = metricsData?.filter((e) => e.eventDate === dateKey) || []
                const categoryEvents = selectedCategory
                  ? dayEvents.filter((e) => e.category === selectedCategory)
                  : dayEvents

                if (categoryEvents.length > 0) {
                  return "has-events"
                }
                return ""
              }}
            />
          </div>

          {/* Selected Date Details */}
          <div className="mt-4 pt-4 border-t">
            <h3 className="text-sm font-semibold mb-2">{dayjs(selectedDate).format("dddd, MMMM D, YYYY")}</h3>
            {(() => {
              const dateKey = dayjs(selectedDate).format("YYYY-MM-DD")
              const dayEvents = metricsData?.filter((e) => e.eventDate === dateKey) || []
              const categoryEvents = selectedCategory
                ? dayEvents.filter((e) => e.category === selectedCategory)
                : dayEvents

              if (categoryEvents.length === 0) {
                return <p className="text-sm text-gray-500">No events on this date</p>
              }

              return (
                <div className="space-y-2">
                  {categoryEvents.map((event) => (
                    <div key={event.id} className="border rounded p-2 text-xs">
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <div className="flex items-center gap-1">
                          <span className="text-lg">{getCategoryIcon(event.category)}</span>
                          <span className="font-medium">{event.type}</span>
                          {event.status === "planned" && (
                            <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">planned</span>
                          )}
                          {event.status === "completed" && <span className="text-green-600">✓</span>}
                        </div>
                        <button
                          onClick={() => handleDeleteMetric(event)}
                          disabled={deleteMetricMutation.isLoading}
                          className="text-destructive hover:text-destructive disabled:opacity-50 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Delete metric"
                        >
                          🗑️
                        </button>
                      </div>
                      <p className="text-primary">{formatValue(event)}</p>
                      {event.rawInput && (
                        <p
                          className="text-gray-500 mt-0.5 italic break-words overflow-hidden"
                          style={{ maxHeight: "3em", lineHeight: "1.5em" }}
                        >
                          &quot;{event.rawInput}&quot;
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>
        </div>
      ) : (
        // List View
        selectedCategory &&
        categorizedMetrics[selectedCategory] && (
          <div className="bg-background rounded-lg shadow-md p-3 sm:p-4">
            <h2 className="text-primary sm:text-lg font-semibold mb-2 capitalize flex items-center gap-2">
              <span className="text-lg">{getCategoryIcon(selectedCategory)}</span>
              {selectedCategory} Metrics
            </h2>

            {/* Type Distribution */}
            <div className="mb-3">
              <h3 className="text-xs font-medium text-primary mb-1">Type Distribution</h3>
              <div className="flex flex-wrap gap-1">
                {Object.entries(categorizedMetrics[selectedCategory].types).map(([type, count]) => (
                  <span key={type} className="px-2 py-0.5 bg-foregroundx rounded-full text-xs">
                    {type}: {count}
                  </span>
                ))}
              </div>
            </div>

            {/* Events Grouped by Week */}
            <div>
              <h3 className="text-xs font-medium text-primary mb-2">Events Timeline</h3>
              <div className="space-y-4">
                {Object.entries(groupedByWeek)
                  .sort((a, b) => b[0].localeCompare(a[0]))
                  .map(([weekStart, weekDays]) => {
                    const weekStartDate = dayjs(weekStart)
                    const weekEndDate = weekStartDate.endOf("isoWeek")

                    return (
                      <div key={weekStart} className="border rounded-lg p-3 bg-foregroundx">
                        <h4 className="text-sm font-semibold mb-2 text-gray-800">
                          Week of {weekStartDate.format("MMM D")} - {weekEndDate.format("MMM D, YYYY")}
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(weekDays)
                            .sort((a, b) => b[0].localeCompare(a[0]))
                            .map(([day, events]) => {
                              const dayDate = dayjs(day)
                              const plannedCount = events.filter((e) => e.status === "planned").length
                              const completedCount = events.filter((e) => e.status === "completed").length

                              return (
                                <div key={day} className="bg-background rounded p-2 border border-border">
                                  <div className="flex justify-between items-center mb-1">
                                    <h5 className="text-sm font-medium text-primary">
                                      {dayDate.format("dddd, MMM D")}
                                    </h5>
                                    <div className="flex gap-2 text-xs">
                                      {plannedCount > 0 && (
                                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                          {completedCount}/{plannedCount} planned
                                        </span>
                                      )}
                                      <span className="text-gray-500">{events.length} total</span>
                                    </div>
                                  </div>
                                  <div className="space-y-1">
                                    {events.map((event) => (
                                      <div
                                        key={event.id}
                                        className="border-l-2 border-border pl-2 py-1  transition-colors"
                                      >
                                        <div className="flex justify-between items-start gap-2">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-1">
                                              <span className="font-medium text-xs">
                                                {event.data.task_name || event.type}
                                              </span>
                                              {event.status === "planned" && (
                                                <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">
                                                  planned
                                                </span>
                                              )}
                                              {event.status === "completed" && (
                                                <span className="text-green-600 text-xs">✓</span>
                                              )}
                                              {event.confidence && (
                                                <span className="text-gray-500 text-xs">
                                                  ({Math.round(parseFloat(event.confidence) * 100)}%)
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-primary text-xs">{formatValue(event)}</p>
                                            {event.rawInput && (
                                              <p
                                                className="text-gray-500 mt-0.5 italic break-words overflow-hidden text-xs"
                                                style={{ maxHeight: "3em", lineHeight: "1.5em" }}
                                              >
                                                &quot;{event.rawInput}&quot;
                                              </p>
                                            )}
                                            {event.tags.length > 0 && (
                                              <div className="flex flex-wrap gap-0.5 mt-0.5">
                                                {event.tags.map((tag, idx) => (
                                                  <span
                                                    key={idx}
                                                    className="bg-blue-100 text-blue-700 px-1 py-0.5 rounded text-xs"
                                                  >
                                                    {tag}
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                            <div className="text-gray-500 text-xs">
                                              {dayjs(event.eventTime).format("h:mm A")}
                                            </div>
                                            <button
                                              onClick={() => handleDeleteMetric(event)}
                                              disabled={deleteMetricMutation.isLoading}
                                              className="text-destructive hover:text-destructive disabled:opacity-50 p-1 rounded hover:bg-red-50 transition-colors text-xs"
                                              title="Delete metric"
                                            >
                                              🗑️
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
        )
      )}

      {/* Empty State */}
      {categories.length === 0 && (
        <div className="bg-foregroundx rounded-lg p-6 sm:p-8 text-center">
          <span className="text-4xl mb-2 block">📊</span>
          <h3 className="text-base font-medium  mb-1">No metrics found</h3>
          <p className="text-sm ">Start tracking your metrics in Telegram to see them here.</p>
        </div>
      )}
    </div>
  )
}

export default MetricsScreen
